'use client';

import { useGesture } from '@use-gesture/react';
import { animate, useMotionValue, useMotionValueEvent } from 'motion/react';
import { useCallback, useRef, useState } from 'react';
import { containHorizontalNavigation } from '@/utils/horizontalNavigation';
import { TICK_SPACING } from './useCompassScale';

const PIXELS_PER_TICK = 20;

export const useCompassInput = () => {
  const compassRef = useRef<HTMLDivElement>(null);
  const [activeTick, setActiveTick] = useState(0);
  const position = useMotionValue(0);
  useMotionValueEvent(position, 'change', (value) => {
    setActiveTick(Math.round(value));
  });

  const cancelSnap = useCallback(() => position.stop(), [position]);

  const snap = () => {
    const target = Math.round(position.get());
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      position.set(target);
      return;
    }
    animate(position, target, { duration: 0.1, ease: 'easeOut' });
  };

  const moveBy = (ticks: number, finished: boolean) => {
    cancelSnap();
    position.set(position.get() + ticks);
    if (finished) snap();
  };

  useGesture(
    {
      onWheel: ({ event, delta: [x], last }) => {
        if (event.ctrlKey || event.metaKey) return;
        if (event.cancelable) event.preventDefault();
        moveBy(last ? 0 : x / PIXELS_PER_TICK, last);
      },
      onDrag: ({ delta: [x], last }) => moveBy(-x / TICK_SPACING, last),
    },
    {
      target: compassRef,
      eventOptions: { passive: false, capture: true },
      drag: { axis: 'x', threshold: 0, pointer: { keys: false } },
    }
  );

  const attachCompass = useCallback(
    (compass: HTMLDivElement | null) => {
      compassRef.current = compass;
      if (!compass) return;

      const releaseNavigation = containHorizontalNavigation(compass);
      return () => {
        releaseNavigation();
        cancelSnap();
        compassRef.current = null;
      };
    },
    [cancelSnap]
  );

  return { compassRef, attachCompass, position, activeTick };
};
